import {Form, FormTemplateResponse, RosterFormCodeBinding} from "@/api/dto/forms";
import SlideInPane from "patient-ping-remedy/packages/slide-in-pane";
import SurveyJSForm from "@common/survey_js/SurveyJSForm";
import {useContext, useEffect, useState} from "react";
import {SurveyModel} from "survey-core";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {AxiosError} from "axios";
import Helpers from "@/helpers/helpers";
import {GENERICERROR} from "@manual_encounters/constants/BannerMessages";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {isNull, omitBy} from "lodash-es";
import {useAlertStore} from "@/store/alert_store";
import {MixpanelEventType, MixpanelFormSubmitEvent} from "@/api/dto/mixpanel";
import {useActiveRoster} from "@/store/roster_store";
import {useMixpanel} from "@/hooks/useMixpanel";
import Button from "patient-ping-remedy/packages/button";
import {ButtonWrapper} from "@clients/profile_page/forms_tab/FormDetailsDrawer.styles";
import Loading from "@common/Loading";

type Props = {
    rosterCodeBinding: RosterFormCodeBinding;
    formTemplate: FormTemplateResponse;
    close(): void;
    open: boolean;
    patientId: string;
    form?: Form;
}

const RosterBindingFormName = {
  [RosterFormCodeBinding.CLIENT_ADDITIONAL_DETAILS] : 'Additional details',
  [RosterFormCodeBinding.REFERRAL_TREATMENT_DETAILS] : 'Referral Treatment details',
  [RosterFormCodeBinding.TEST_1]: 'Test 1',
  [RosterFormCodeBinding.TEST_2]: 'Test 2',
};

const RosterBindingFormDrawer = (props: Props) => {
  const {carecoApi} = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const queryClient = useQueryClient();
  const {addAlert} = useAlertStore();
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const [surveyModel, setSurveyModel] = useState<SurveyModel | null>();
  const [isFormDataLoading, setIsFormDataLoading] = useState(false);

  const { mutate: submitForm, isPending } = useMutation({
    mutationFn: async () => props.form
      ? carecoApi?.putTemplateForm(
        currentRoster?.id ?? 0,
        props.patientId,
        props.form.id,
        JSON.stringify(omitBy(surveyModel?.getData(), isNull)),
      )
      : carecoApi?.postTemplateForm(
        currentRoster?.id ?? 0,
        props.patientId,
        props.formTemplate.id,
        JSON.stringify(omitBy(surveyModel?.getData(), isNull))
      ),
    onSuccess: () => {
      addAlert({content: 'Form save successfully.', type: ToastType.SUCCESS});
      void queryClient.invalidateQueries({
        queryKey: ['form', props.patientId, currentRoster?.id, RosterFormCodeBinding.CLIENT_ADDITIONAL_DETAILS]
      });
      postMixpanelEvent();
      props.close();
    },
    onError: (error: AxiosError) => {
      Helpers.alertOnError(
        `Failed to save form. ${GENERICERROR} ${Helpers.traceId(error)}`,
        error);
    },
  })  ;

  const postMixpanelEvent = () => {
    if (currentRoster && mixpanelEventProperties) {
      const event: MixpanelFormSubmitEvent = {
        eventName:  MixpanelEventType.BIH_FORM_SAVED,
        ...mixpanelEventProperties,
        ...getCurrentGroupDetails(currentRoster),
        clientId: props.patientId,
        codeBinding: props.rosterCodeBinding,
      };
      carecoApi?.postMixpanelEvent(event);
    }
  };

  useEffect(() => {
    if(props.open) {
      setSurveyModel(new SurveyModel(props.formTemplate.surveyJs));
    } else {
      surveyModel?.dispose();
      setSurveyModel(null);
    }
  }, [props.formTemplate, props.open]);

  useEffect(() => {
    if(surveyModel && props.form) {
      setIsFormDataLoading(true);
      carecoApi?.getSurveyFormData(props.form.id, currentRoster?.id ?? 0).then(formData => {
        if(formData) {
          surveyModel.mergeData(formData);
        }
      }).finally(() => setIsFormDataLoading(false));
    }
  }, [props.form, surveyModel, currentRoster]);

  const isLoading = isFormDataLoading;

  return (
    <SlideInPane
      isOpen={props.open}
      onClose={props.close}
      headerText={RosterBindingFormName[props.rosterCodeBinding]}
      showNav={false}
      headerChildren={
        <ButtonWrapper>
          <Button onClick={() => submitForm()} disabled={isPending || isLoading}>
            Save
          </Button>
        </ButtonWrapper>
      }
    >
      {isLoading && <Loading />}
      {surveyModel && !isLoading && <SurveyJSForm surveyModel={surveyModel} addNpi={false}/>}
    </SlideInPane>
  );
};

export default RosterBindingFormDrawer;
